/** Ocultar campos de impresora física en el formulario SucVenta (no se usan por ahora). */
export const MOSTRAR_CAMPOS_IMPRESORA = false;

/** Altura mínima compartida entre tarjetas de letra y notas en la solapa Impresión. */
export const IMPRESION_TARJETA_MIN_HEIGHT = 216;

export const IMPRESION_MOTOR = {
  PLANTILLA: 'PLANTILLA',
  RDLC: 'RDLC',
  /** Legacy: se interpreta como RDLC al leer Planti*. */
  CRYSTAL: 'CRYSTAL',
} as const;

export const IMPRESION_VARIANTE = {
  PLANTILLA_1: 'PLANTILLA_1',
  PLANTILLA_2: 'PLANTILLA_2',
  PLANTILLA_3: 'PLANTILLA_3',
} as const;

export type ImpresionMotor = (typeof IMPRESION_MOTOR)[keyof typeof IMPRESION_MOTOR];
export type ImpresionVariante = (typeof IMPRESION_VARIANTE)[keyof typeof IMPRESION_VARIANTE];

/** Tipo formulario recibo en SucVenta / Formulario. */
export const NRO_TIPO_FORMULARIO_RECIBO = '4';

export type VariantePlantillaUi = {
  valor: string;
  etiqueta: string;
  ayuda: string;
};

const VARIANTES_FACTURA: VariantePlantillaUi[] = [
  {
    valor: IMPRESION_VARIANTE.PLANTILLA_1,
    etiqueta: 'Plantilla 1 — Original y Duplicado (misma página)',
    ayuda: 'Se imprimen Original y Duplicado en la misma página.',
  },
  {
    valor: IMPRESION_VARIANTE.PLANTILLA_2,
    etiqueta: 'Plantilla 2 — Diseño legacy',
    ayuda: 'Diseño legacy: una sola copia, sin etiquetas Original/Duplicado.',
  },
  {
    valor: IMPRESION_VARIANTE.PLANTILLA_3,
    etiqueta: 'Plantilla 3 — Original y Duplicado (páginas distintas)',
    ayuda: 'Se imprimen Original y Duplicado en páginas distintas.',
  },
];

/** En recibos, 2 y 3 están invertidos respecto de facturas. */
const VARIANTES_RECIBO: VariantePlantillaUi[] = [
  {
    valor: IMPRESION_VARIANTE.PLANTILLA_1,
    etiqueta: 'Plantilla 1 — Diseño legacy',
    ayuda: 'Diseño legacy: una sola copia, sin etiquetas Original/Duplicado.',
  },
  {
    valor: IMPRESION_VARIANTE.PLANTILLA_2,
    etiqueta: 'Plantilla 2 — Original y Duplicado (páginas distintas)',
    ayuda: 'Se imprimen Original y Duplicado en páginas distintas.',
  },
  {
    valor: IMPRESION_VARIANTE.PLANTILLA_3,
    etiqueta: 'Plantilla 3 — Original y Duplicado (misma página)',
    ayuda: 'Se imprimen Original y Duplicado en la misma página.',
  },
];

export function variantesPlantillaParaTipo(
  nroTipoFormulario?: string | null,
): VariantePlantillaUi[] {
  const tipo = String(nroTipoFormulario ?? '').trim();
  return tipo === NRO_TIPO_FORMULARIO_RECIBO ? VARIANTES_RECIBO : VARIANTES_FACTURA;
}

export function ayudaVariantePlantilla(
  variante: string | null | undefined,
  nroTipoFormulario?: string | null,
): string {
  const valor = (variante || IMPRESION_VARIANTE.PLANTILLA_1).trim().toUpperCase();
  const found = variantesPlantillaParaTipo(nroTipoFormulario).find((v) => v.valor === valor);
  return found?.ayuda ?? '';
}

export function esMotorRdlc(motor?: string | null): boolean {
  const m = (motor ?? '').trim().toUpperCase();
  return m === IMPRESION_MOTOR.RDLC || m === IMPRESION_MOTOR.CRYSTAL;
}

/** Extrae la ruta del reporte (.rdlc / legacy .rpt) desde el valor de Planti*. */
export function extraerRutaRdlcDesdePlanti(planti?: string | null): string {
  const raw = (planti ?? '').trim();
  if (!raw) return '';

  const upper = raw.toUpperCase();
  if (
    upper.startsWith(`${IMPRESION_MOTOR.RDLC}|`) ||
    upper.startsWith(`${IMPRESION_MOTOR.CRYSTAL}|`)
  ) {
    return raw.slice(raw.indexOf('|') + 1).trim();
  }

  if (upper.startsWith(`${IMPRESION_MOTOR.PLANTILLA}|`)) {
    for (const markerMotor of [IMPRESION_MOTOR.RDLC, IMPRESION_MOTOR.CRYSTAL]) {
      const marker = `|${markerMotor}|`;
      const idx = upper.indexOf(marker);
      if (idx >= 0) {
        return raw.slice(idx + marker.length).trim();
      }
    }
    return '';
  }

  // Legacy: el campo Planti* guardaba directamente la ruta del reporte
  return raw;
}

export function serializarPlantiRdlc(rutaRdlc?: string | null): string {
  const ruta = (rutaRdlc ?? '').trim();
  if (!ruta) return '';
  const upper = ruta.toUpperCase();
  if (upper.startsWith(`${IMPRESION_MOTOR.RDLC}|`) || upper.startsWith(`${IMPRESION_MOTOR.CRYSTAL}|`)) {
    return ruta;
  }
  return `${IMPRESION_MOTOR.RDLC}|${ruta}`;
}

export type ImpresionLetraState = {
  motor: ImpresionMotor;
  variantePlantilla?: ImpresionVariante | string | null;
  rutaRdlc?: string | null;
};

/** Combina estado previo, cambio parcial y respaldo legacy sin perder rutaRdlc. */
export function mergeImpresionLetra(
  prev: ImpresionLetraState | undefined,
  next: Partial<ImpresionLetraState>,
  plantiFallback?: string | null,
): ImpresionLetraState {
  const rutaRespaldo = extraerRutaRdlcDesdePlanti(plantiFallback);
  const rutaRdlc =
    next.rutaRdlc?.trim() || prev?.rutaRdlc?.trim() || rutaRespaldo || '';

  const motorRaw = next.motor ?? prev?.motor ?? IMPRESION_MOTOR.PLANTILLA;

  return {
    motor: esMotorRdlc(motorRaw) ? IMPRESION_MOTOR.RDLC : IMPRESION_MOTOR.PLANTILLA,
    variantePlantilla:
      next.variantePlantilla ??
      prev?.variantePlantilla ??
      IMPRESION_VARIANTE.PLANTILLA_1,
    rutaRdlc,
  };
}

/** Normaliza impresión recibida del API (acepta camelCase o PascalCase; compat rutaCrystal). */
export function parseImpresionDesdeApi(raw: unknown): Partial<ImpresionLetraState> {
  if (!raw || typeof raw !== 'object') return {};
  const o = raw as Record<string, unknown>;
  const motorRaw = (o.motor ?? o.Motor) as ImpresionMotor | undefined;
  const ruta =
    String(o.rutaRdlc ?? o.RutaRdlc ?? o.rutaCrystal ?? o.RutaCrystal ?? '').trim() ||
    undefined;
  return {
    motor: motorRaw
      ? esMotorRdlc(motorRaw)
        ? IMPRESION_MOTOR.RDLC
        : IMPRESION_MOTOR.PLANTILLA
      : undefined,
    variantePlantilla: (o.variantePlantilla ?? o.VariantePlantilla) as string | undefined,
    rutaRdlc: ruta,
  };
}
